// The person the user talks to: name pools, pronouns, generation, and the
// validation the trial server applies before interpolating any of it into a
// prompt template.
//
// CommonJS on purpose: this module is loaded both by the React bundle and by
// `server/` (see server/index.js), so it must be requirable from plain Node.

// First-name pools, by presenting gender.
const NAMES = {
  male: ['James', 'Michael', 'David', 'Daniel', 'Matthew', 'Andrew', 'Thomas',
         'Anthony', 'Kevin', 'Brian', 'Jason', 'Steven', 'Liam', 'Noah', 'Oliver',
         'Elijah', 'Lucas', 'Mason', 'Logan', 'Ethan', 'Jacob', 'Marcus', 'Nathan',
         'Adrian', 'Julian', 'Leo', 'Miles', 'Theo', 'Elias', 'Omar'],
  female: ['Emma', 'Olivia', 'Ava', 'Isabella', 'Sophia', 'Charlotte', 'Mia',
           'Amelia', 'Harper', 'Evelyn', 'Jessica', 'Sarah', 'Emily', 'Rachel',
           'Hannah', 'Grace', 'Chloe', 'Zoe', 'Nora', 'Lily', 'Maya', 'Aisha',
           'Priya', 'Elena', 'Clara', 'Ruth', 'Naomi', 'Diana', 'Carmen', 'Ingrid'],
};

// Professions used to flesh out personas where a job is relevant (interviewer,
// networking, support, etc.). Harmless where the persona ignores it.
const PROFESSIONS = [
  'Software Engineer', 'Teacher', 'Nurse', 'Marketing Manager', 'Graphic Designer',
  'Accountant', 'Chef', 'Writer', 'Doctor', 'Lawyer', 'Architect', 'Journalist',
  'Photographer', 'Sales Representative', 'Financial Analyst', 'Social Worker',
  'Entrepreneur', 'Product Manager', 'Consultant', 'Operations Lead',
  'Research Scientist', 'Dentist', 'Real Estate Agent', 'Event Planner',
];

const HAIR_COLORS = ['black', 'brown', 'blonde', 'red', 'dark brown', 'light brown', 'chestnut', 'auburn', 'gray'];
const EYE_COLORS = ['brown', 'blue', 'green', 'hazel', 'gray', 'amber'];
const HEIGHTS = ['short', 'average height', 'tall'];
const BUILDS = ['slender', 'athletic', 'average', 'stocky', 'curvy'];

// Pronoun sets used when filling a persona prompt.
const PRONOUNS = {
  male: { subject: 'he', Subject: 'He', object: 'him', possessive: 'his' },
  female: { subject: 'she', Subject: 'She', object: 'her', possessive: 'her' },
  they: { subject: 'they', Subject: 'They', object: 'them', possessive: 'their' },
};

// The user names their own partner and may describe them, so both fields reach
// the persona prompt as free text. Neither may open a new line or a new
// section: square brackets are how the app passes stage directions to the
// model, and the angle markers below are how a description is fenced off.
const MAX_NAME_CHARS = 32;
const MAX_DESCRIPTION_CHARS = 300;

// The fence a description is wrapped in, so the model can tell the user's words
// from the app's. Nothing inside may reproduce it.
const DESCRIPTION_FENCE = '###';

const flatten = (text) =>
  text
    // eslint-disable-next-line no-control-regex
    .replace(/[\u0000-\u001f\u007f]+/g, ' ')
    .replace(/[[\]]/g, '')
    .replace(/#/g, '')
    .replace(/\s+/g, ' ')
    .trim();

const sanitizeName = (raw) => {
  if (typeof raw !== 'string') return null;
  const name = flatten(raw).slice(0, MAX_NAME_CHARS).trim();
  return name || null;
};

const sanitizeDescription = (raw) => {
  if (raw === undefined || raw === null || raw === '') return '';
  if (typeof raw !== 'string') return null;
  return flatten(raw).slice(0, MAX_DESCRIPTION_CHARS).trim();
};

const getRandomItem = (array) => array[Math.floor(Math.random() * array.length)];

// A description can only steer the avatar through the enums DiceBear accepts,
// so pick out a hair colour if the user named one. The rest of what they wrote
// reaches the look through the avatar seed instead.
const descriptionHairColor = (description) => {
  const text = description.toLowerCase();
  // Longest first, so "dark brown" wins over the "brown" inside it.
  return [...HAIR_COLORS].sort((a, b) => b.length - a.length).find((c) => text.includes(c)) || null;
};

// Build the concrete person for a scenario. `partner` on the scenario constrains
// gender/age and can pin a display name (e.g. "Mom"). `overrides` carries what
// the user typed when they picked the card: the name they want to practice
// against, and optionally a free-text description of who this person is. A
// user-supplied name beats the pinned one, since renaming "Mom" is the point.
const generatePartner = (scenario, overrides = {}) => {
  const spec = scenario.partner;
  const gender = spec.gender === 'either' ? getRandomItem(['male', 'female']) : spec.gender;
  const [minAge, maxAge] = spec.ageRange;
  const age = minAge + Math.floor(Math.random() * (maxAge - minAge + 1));
  const name = sanitizeName(overrides.name) || spec.displayName || getRandomItem(NAMES[gender]);
  const description = sanitizeDescription(overrides.description) || '';

  return {
    name,
    gender,
    age,
    description,
    subtitle: spec.subtitle,
    profession: getRandomItem(PROFESSIONS),
    physical: {
      hairColor: descriptionHairColor(description) || getRandomItem(HAIR_COLORS),
      eyeColor: getRandomItem(EYE_COLORS),
      height: getRandomItem(HEIGHTS),
      build: getRandomItem(BUILDS),
    },
  };
};

// Narrow an untrusted partner object down to something this scenario could
// have produced, or return null if it couldn't have.
//
// The trial server interpolates these fields into a persona template. Gender,
// age, profession and subtitle are therefore taken from a fixed pool or from
// the scenario itself, never from the caller. Name and description are the
// exceptions: the user writes them, so they are flattened to a single line of
// bracket-free text and length-capped, and the description is fenced off in the
// prompt (see buildSystemPrompt) rather than trusted as instruction.
const sanitizePartner = (scenario, raw) => {
  if (!raw || typeof raw !== 'object') return null;
  const spec = scenario.partner;

  const gender = raw.gender === 'male' || raw.gender === 'female' ? raw.gender : null;
  if (!gender) return null;
  if (spec.gender !== 'either' && gender !== spec.gender) return null;

  const age = raw.age;
  const [minAge, maxAge] = spec.ageRange;
  if (!Number.isInteger(age) || age < minAge || age > maxAge) return null;

  const name = sanitizeName(raw.name) || spec.displayName;
  if (!name) return null;

  const description = sanitizeDescription(raw.description);
  if (description === null) return null;

  // The persona only ever mentions a profession where the template asks for
  // one, so an unrecognised value can fall back rather than fail the request.
  const profession = PROFESSIONS.includes(raw.profession) ? raw.profession : PROFESSIONS[0];

  return { name, gender, age, description, profession, subtitle: spec.subtitle };
};

// Fill a scenario's persona template with the partner's details.
const formatScenarioPrompt = (scenario, partner) => {
  const pronouns = PRONOUNS[partner.gender] || PRONOUNS.they;
  const replacements = {
    name: partner.name,
    age: partner.age,
    profession: partner.profession,
    pronoun: pronouns.subject,
    Pronoun: pronouns.Subject,
    object: pronouns.object,
    possessive: pronouns.possessive,
  };

  return scenario.promptTemplate.replace(/{{(\w+)}}/g, (match, key) =>
    key in replacements ? replacements[key] : match
  );
};

module.exports = {
  NAMES,
  PROFESSIONS,
  PRONOUNS,
  DESCRIPTION_FENCE,
  MAX_NAME_CHARS,
  MAX_DESCRIPTION_CHARS,
  sanitizeName,
  sanitizeDescription,
  generatePartner,
  sanitizePartner,
  formatScenarioPrompt,
};
